/**
 * FactoryJourneyModel (AJIP-16.02.009 · §15, §16, §17)
 * ---------------------------------------------------------------------
 * 6 canonical stages, canonical command alphabet, deterministic
 * transition table, stage→engine state map (F5), journey status enum
 * (F5), and status→engine mapping.
 *
 * Directive requirements honoured:
 *   §15 — 6 stage names verbatim.
 *   §15.6 — FACTORY_JOURNEY_READY is the terminal foundation state.
 *   §16 — every transition validated; backward transitions permitted
 *         only via authorised command (RETURN_FACTORY_STAGE_FOR_CORRECTION).
 *   §17 — canonical command names.
 *   §19.1 — journey statuses limited to certified STATES.
 *   F7  — RETURN_FACTORY_STAGE_FOR_CORRECTION included with permission
 *         semantics enforced by the command handler layer.
 */
import { STATES } from '../core/JourneyState';

const FACTORY_STAGES = Object.freeze({
  FACTORY_IDENTITY: 'FACTORY_IDENTITY',
  FACTORY_PROFILE: 'FACTORY_PROFILE',
  CAPABILITY_DECLARATION: 'CAPABILITY_DECLARATION',
  EVIDENCE_READINESS: 'EVIDENCE_READINESS',
  OPERATIONAL_READINESS: 'OPERATIONAL_READINESS',
  FACTORY_JOURNEY_READY: 'FACTORY_JOURNEY_READY',
});
const ALL_FACTORY_STAGES = Object.freeze(Object.values(FACTORY_STAGES));

// F7 — canonical command alphabet including authorised backward-navigation.
const FACTORY_COMMANDS = Object.freeze({
  START_FACTORY_JOURNEY: 'START_FACTORY_JOURNEY',
  RECORD_FACTORY_IDENTITY: 'RECORD_FACTORY_IDENTITY',
  RECORD_FACTORY_PROFILE: 'RECORD_FACTORY_PROFILE',
  DECLARE_FACTORY_CAPABILITIES: 'DECLARE_FACTORY_CAPABILITIES',
  RECORD_FACTORY_EVIDENCE_READINESS: 'RECORD_FACTORY_EVIDENCE_READINESS',
  CONFIRM_FACTORY_OPERATIONAL_READINESS: 'CONFIRM_FACTORY_OPERATIONAL_READINESS',
  COMPLETE_FACTORY_FOUNDATION: 'COMPLETE_FACTORY_FOUNDATION',
  RESUME_FACTORY_JOURNEY: 'RESUME_FACTORY_JOURNEY',
  RETURN_FACTORY_STAGE_FOR_CORRECTION: 'RETURN_FACTORY_STAGE_FOR_CORRECTION',
});
const ALL_FACTORY_COMMANDS = Object.freeze(Object.values(FACTORY_COMMANDS));

// F5 — canonical journey statuses mapped to certified engine STATES.
const FACTORY_STATUSES = Object.freeze({
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  BLOCKED: 'BLOCKED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
});
const STATUS_TO_ENGINE_STATE = Object.freeze({
  ACTIVE: STATES.IN_PROGRESS,
  PAUSED: STATES.WAITING,
  BLOCKED: STATES.BLOCKED,
  COMPLETED: STATES.COMPLETED,
  CANCELLED: STATES.CANCELLED,
});

const STAGE_TO_ENGINE_STATE = Object.freeze({
  FACTORY_IDENTITY: STATES.STARTED,
  FACTORY_PROFILE: STATES.IN_PROGRESS,
  CAPABILITY_DECLARATION: STATES.IN_PROGRESS,
  EVIDENCE_READINESS: STATES.IN_PROGRESS,
  OPERATIONAL_READINESS: STATES.IN_PROGRESS,
  FACTORY_JOURNEY_READY: STATES.COMPLETED,
});

// Forward canonical progression.
const FACTORY_STAGE_TRANSITIONS = Object.freeze([
  {
    from: FACTORY_STAGES.FACTORY_IDENTITY,
    command: FACTORY_COMMANDS.RECORD_FACTORY_IDENTITY,
    to: FACTORY_STAGES.FACTORY_PROFILE, persist: true, direction: 'forward',
  },
  {
    from: FACTORY_STAGES.FACTORY_PROFILE,
    command: FACTORY_COMMANDS.RECORD_FACTORY_PROFILE,
    to: FACTORY_STAGES.CAPABILITY_DECLARATION, persist: true, direction: 'forward',
  },
  {
    from: FACTORY_STAGES.CAPABILITY_DECLARATION,
    command: FACTORY_COMMANDS.DECLARE_FACTORY_CAPABILITIES,
    to: FACTORY_STAGES.EVIDENCE_READINESS, persist: true, direction: 'forward',
  },
  {
    from: FACTORY_STAGES.EVIDENCE_READINESS,
    command: FACTORY_COMMANDS.RECORD_FACTORY_EVIDENCE_READINESS,
    to: FACTORY_STAGES.OPERATIONAL_READINESS, persist: true, direction: 'forward',
  },
  {
    from: FACTORY_STAGES.OPERATIONAL_READINESS,
    command: FACTORY_COMMANDS.CONFIRM_FACTORY_OPERATIONAL_READINESS,
    to: FACTORY_STAGES.FACTORY_JOURNEY_READY,
    persist: true, direction: 'forward', engineTransition: 'complete',
  },
]);

// F7 — permitted backward corrections (any earlier stage from any later
// non-terminal stage; terminal FACTORY_JOURNEY_READY is NOT correctable
// via this command per §16.3).
const RETURN_FOR_CORRECTION_MATRIX = Object.freeze({
  FACTORY_PROFILE: [FACTORY_STAGES.FACTORY_IDENTITY],
  CAPABILITY_DECLARATION: [
    FACTORY_STAGES.FACTORY_IDENTITY, FACTORY_STAGES.FACTORY_PROFILE,
  ],
  EVIDENCE_READINESS: [
    FACTORY_STAGES.FACTORY_IDENTITY, FACTORY_STAGES.FACTORY_PROFILE,
    FACTORY_STAGES.CAPABILITY_DECLARATION,
  ],
  OPERATIONAL_READINESS: [
    FACTORY_STAGES.FACTORY_IDENTITY, FACTORY_STAGES.FACTORY_PROFILE,
    FACTORY_STAGES.CAPABILITY_DECLARATION, FACTORY_STAGES.EVIDENCE_READINESS,
  ],
});

const findForwardTransition = (fromStage, command) =>
  FACTORY_STAGE_TRANSITIONS.find((t) => t.from === fromStage && t.command === command) || null;

const canReturnForCorrection = (fromStage, toStage) => {
  const permitted = RETURN_FOR_CORRECTION_MATRIX[fromStage];
  return Array.isArray(permitted) && permitted.includes(toStage);
};

export {
  FACTORY_STAGES, ALL_FACTORY_STAGES,
  FACTORY_COMMANDS, ALL_FACTORY_COMMANDS,
  FACTORY_STATUSES, STATUS_TO_ENGINE_STATE,
  STAGE_TO_ENGINE_STATE, FACTORY_STAGE_TRANSITIONS,
  RETURN_FOR_CORRECTION_MATRIX,
  findForwardTransition, canReturnForCorrection,
};
